import { ReactNode } from "react";
import { Box, CircularProgress, Paper, Typography, alpha } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTheme } from "../../hooks";
import { toSurfaceTint } from "../../utils/colorManager";
import CopyButton from "../CopyButton";

export type OutcomeTone = "pending" | "success" | "error";

/**
 * The one frame the awaiting, success and failure screens share: a centred
 * card, a status disc, a heading, the total, the body, then the actions.
 * Only the pending disc is branded; success and failure stay neutral so a
 * tenant palette can never make a failure look green.
 */
export const OutcomeCard = ({
  tone,
  eyebrow,
  title,
  total,
  children,
  actions,
}: {
  tone: OutcomeTone;
  eyebrow?: string;
  title: string;
  total?: string;
  children?: ReactNode;
  actions?: ReactNode;
}) => {
  const { theme } = useTheme();

  return (
    <Box sx={{ width: "100%", maxWidth: 540, mx: "auto", px: 2, py: 3 }}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, p: { xs: 3, sm: 4 }, textAlign: "center" }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            mx: "auto",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: muiTheme =>
              tone === "pending"
                ? toSurfaceTint(theme.color_secondary)
                : alpha(muiTheme.palette[tone].main, 0.12),
          }}
        >
          {tone === "pending" && (
            // Named by the card's own translated heading.
            <CircularProgress size={30} color="primary" aria-label={title} />
          )}
          {tone === "success" && <CheckRoundedIcon color="success" />}
          {tone === "error" && <CloseRoundedIcon color="error" />}
        </Box>
        {eyebrow && (
          <Typography
            variant="overline"
            component="p"
            sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 2 }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        {total && (
          <Typography sx={{ fontSize: 32, fontWeight: 800, my: 1 }}>
            {total}
          </Typography>
        )}
        {children}
        {actions && (
          <Box
            sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {actions}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

/** Label and value rows, a value only when there is one to show. */
export const OutcomeRows = ({
  label,
  rows,
}: {
  label: string;
  rows: { term: string; value?: string }[];
}) => {
  const shown = rows.filter(row => row.value);
  if (shown.length === 0) return null;

  return (
    <Box component="dl" aria-label={label} sx={{ my: 2, textAlign: "left" }}>
      {shown.map(row => (
        <Box
          key={row.term}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography component="dt" sx={{ fontSize: 14, color: "text.secondary" }}>
            {row.term}
          </Typography>
          <Typography
            component="dd"
            sx={{ m: 0, fontSize: 14, fontWeight: 600, textAlign: "right" }}
          >
            {row.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

/** A reference the payer quotes, set in a copyable mono chip. */
export const ReferenceBlock = ({
  label,
  value,
  copyLabel,
  help,
}: {
  label: string;
  value: string;
  copyLabel?: string;
  help?: string;
}) => (
  <Box sx={{ my: 2 }}>
    <Typography
      variant="overline"
      component="p"
      sx={{ color: "text.secondary", fontWeight: 700, lineHeight: 2 }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        maxWidth: "100%",
        px: 1.5,
        py: 0.25,
        border: 1,
        borderColor: "divider",
        borderRadius: 1.5,
        bgcolor: "action.hover",
      }}
    >
      <Typography
        sx={{
          fontFamily: "monospace",
          fontWeight: 700,
          fontSize: 14,
          // No spaces to break on, and the card is narrow on a phone.
          wordBreak: "break-all",
          userSelect: "text",
        }}
      >
        {value}
      </Typography>
      {copyLabel && <CopyButton value={value} label={copyLabel} />}
    </Box>
    {help && (
      <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
        {help}
      </Typography>
    )}
  </Box>
);

export default OutcomeCard;
